import type { ReactNode } from 'react';
import type { AssetKey } from '@/assets/manifest';
import { Section } from '@/components/layout';
import { Reveal } from '@/components/motion';
import { GoldLine, OrnamentIcon } from '@/components/ornaments';
import type { OrnamentIconName } from '@/components/ornaments';
import { TicketCta } from '@/components/ui';
import { Eyebrow } from '@/components/typography';
import { formatDateRangeLong, getEventDayCount, type EventSlug, type TourEvent } from '@/features/events';
import { AssetImage } from './AssetImage';
import styles from './ConversionBlocks.module.css';

/* =============================================================================
 * BLOQUES DE CONVERSIÓN · encargo del cliente del 28-jul-2026 (vídeo de Loom
 * «Revisión landing gira 2027», revisado con Simón sobre `/buenos-aires`).
 * -----------------------------------------------------------------------------
 * Lo que pidieron, literal, y dónde cae cada cosa:
 *
 *   «debajo de "recupera tu autoestima" [automaestría, la última línea de la
 *    promesa del hero] nos gustaría ya meter un CTA de compra, es decir,
 *    reserva, QUE TENGA MUCHO DETALLE»                        -> `ReservaCta`
 *   «justo debajo del CTA también meter el POR QUÉ IR, y explicar bien pues
 *    por la Cámara Cuántica, por Azrahel... que la gente pueda ver la foto»
 *                                                             -> `PorQueIr`
 *   «repetir la estructura en las diferentes ciudades, ADAPTANDO EL TEXTO»
 *
 * Esa última frase es la que gobierna el diseño de estos dos componentes, y por
 * eso NO llevan copy propio: el texto sale entero de `src/features/events`, así
 * que cada ciudad se adapta sola y no hay cinco redacciones que mantener en
 * paralelo (ni cinco sitios donde equivocarse cuando el cliente cambie un dato).
 *
 * ── EL HALLAZGO QUE RESUELVE EL «POR QUÉ IR» ─────────────────────────────────
 * No hubo que escribir el argumentario: **ya estaba escrito, por el cliente, y
 * no se pintaba en ninguna parte**. `salesHook` (doc 02 §2 del brief) es un
 * párrafo por ciudad —«Ya te diste cuenta de que las fórmulas espirituales
 * genéricas no funcionan…»— y hasta hoy sólo salía en `/cancun` para Cancún y en
 * `/miami` para… Cancún también. Buenos Aires, Bogotá, México y la propia Miami
 * nunca mostraban el suyo. Es exactamente el «por qué ir» que piden, con la voz
 * del cliente en vez de la mía, y adaptado por ciudad de origen.
 *
 * ADR-003 INTACTO: ni un precio, ni un aforo, ni una sede inventada. Lo que no
 * existe se dice que se anunciará, y el precio vive en la tienda, que es su
 * sitio (orden expresa del ORACLE: «no necesitas poner precios en la landing»).
 * ========================================================================== */

/**
 * La foto de la Cámara Cuántica DE CADA SEDE.
 *
 * El cliente lo pidió con estas palabras: «explicar bien pues por la cámara
 * cuántica, por Azrahel… que la gente pueda ver la foto, la imagen». Las cinco
 * existen ya en el manifiesto, una por ciudad, así que el bloque enseña la de la
 * ciudad en la que está y no una genérica repetida cinco veces.
 *
 * Cancún no tiene domo propio: su Cámara es al atardecer, en la playa, y ésa es
 * la miniatura que la retrata (`thumb-camara-atardecer`).
 */
/* PARCIAL desde el 23-sep-2026: Funes es una convivencia en Alma Sana y su
 * flyer NO anuncia Camara Cuantica. Un destino sin foto de Camara pinta el
 * bloque sin foto antes que pedir prestada la de otra ciudad. */
const CAMARA_POR_CIUDAD: Readonly<Partial<Record<EventSlug, AssetKey>>> = {
  'buenos-aires': 'buenos-aires/camara-domo',
  bogota: 'bogota/camara',
  mexico: 'mexico/camara',
  cancun: 'cancun/thumb-camara-atardecer',
};

/**
 * La franja horaria REAL del destino, leída del programa.
 *
 * Se calcula, no se teclea: si mañana el cliente mueve un bloque, este dato se
 * mueve con él. `end` puede ser `null` a propósito (un bloque abierto, como la
 * cena de llegada de Cancún a las 20:00), así que con todos los finales abiertos
 * se rotula «Desde las HH:mm» en vez de inventarse una hora de cierre.
 */
function franjaHoraria(event: TourEvent): string {
  const inicios = event.program.flatMap((day) => day.blocks.map((block) => block.start));
  const finales = event.program
    .flatMap((day) => day.blocks.map((block) => block.end))
    .filter((end): end is string => end !== null);

  if (inicios.length === 0) return 'Horario por anunciar';
  const primero = inicios.reduce((a, b) => (b < a ? b : a));
  if (finales.length === 0) return `Desde las ${primero}`;
  return `De ${primero} a ${finales.reduce((a, b) => (b > a ? b : a))}`;
}

interface Detalle {
  readonly id: string;
  readonly icon: OrnamentIconName;
  readonly etiqueta: string;
  readonly valor: string;
}

function detallesDe(event: TourEvent): readonly Detalle[] {
  const dias = getEventDayCount(event);
  return [
    {
      id: 'fechas',
      icon: 'moon',
      etiqueta: 'Fechas',
      valor: formatDateRangeLong(event),
    },
    {
      id: 'formato',
      icon: 'star',
      etiqueta: 'Formato',
      /* «VIP» Y «EN PETIT COMITE» SOLO DONDE LOS HAY: Cancun, que es donde el
       * cliente vende las dos cosas y cuyo rotulo NO se toca. Funes tambien es
       * una convivencia, pero nadie ha anunciado ni categoria VIP ni aforo, asi
       * que se rotula lo unico que se sabe: que es una convivencia y cuanto
       * dura. */
      valor:
        event.format === 'retiro'
          ? event.isVip
            ? `Convivencia VIP de ${dias} días, en petit comité`
            : `Convivencia de ${dias} días`
          : `Conferencia presencial de ${dias} días`,
    },
    {
      id: 'horario',
      icon: 'sun',
      etiqueta: 'Horario',
      valor: franjaHoraria(event),
    },
    {
      /* ADR-003 §1: la sede de la mayoría de destinos NO existe todavía. Se dice,
       * no se omite y no se rellena con un nombre plausible. */
      id: 'sede',
      icon: 'mountain',
      etiqueta: 'Sede',
      valor: event.venue ?? 'Se anunciará antes del evento',
    },
  ];
}

export interface ReservaCtaProps {
  event: TourEvent;
  /** `premium` (oro) sólo en Cancún, que es el techo del embudo. */
  variant?: 'standard' | 'premium';
  /** Fondo de la banda. Se alterna con el de la sección de al lado. */
  background?: 'surface' | 'alt';
  /** Sufijo del `id` del título: dos instancias en la misma página no pueden chocar. */
  slot?: string;
}

/**
 * CTA DE COMPRA «CON MUCHO DETALLE», justo debajo de la promesa del hero.
 *
 * POR QUÉ CON DETALLE Y NO UN BOTÓN SUELTO. Quien llega por un anuncio de
 * campaña cae aquí sin haber visto el resto de la página —las campañas van
 * separadas por país a cada ciudad, lo dijeron ellos— y lo primero que necesita
 * para decidir no es un botón: es saber QUÉ compra. Cuatro datos duros (fechas,
 * formato, horario, sede) y el botón al lado. Los cuatro salen del dato tipado.
 *
 * El precio NO está aquí a propósito: vive en la ficha de la tienda, y ponerlo
 * en dos sitios es garantizar que un día se contradigan.
 */
export function ReservaCta({
  event,
  variant = 'standard',
  background = 'alt',
  slot = 'hero',
}: ReservaCtaProps) {
  const tituloId = `reserva-${event.slug}-${slot}`;
  const detalles = detallesDe(event);

  return (
    <Section background={background} labelledBy={tituloId} width="default" className={styles.reservaSection}>
      <Reveal>
        <div className={styles.reserva}>
          <div className={styles.reservaCopy}>
            <Eyebrow ink="gold">
              {event.flag} {event.city}
            </Eyebrow>
            <h2 className={styles.reservaTitulo} id={tituloId}>
              Reserva tu lugar en {event.shortName}
            </h2>
            <GoldLine ornament="diamond" width="sm" className={styles.reservaFilete} />
            {/* AQUÍ IBA `event.tagline` Y ERA UNA REPETICIÓN LITERAL. El hero
             * termina con esa misma frase —`promiseLines` es el `tagline`
             * partido en líneas, comprobado en los cinco destinos— así que el
             * bloque abría diciendo, tres renglones más abajo, exactamente lo
             * que el visitante acababa de leer: «Rompe el motor del ego y las
             * repeticiones de la Matrix. Recupera tu automaestría.»
             *
             * Un eco así no refuerza: hace que el CTA parezca un trozo del hero
             * repetido por error, y encima empuja la tarjeta hacia abajo y la
             * separa de la promesa de la que tiene que colgar. Lo que va aquí es
             * lo que el hero NO dice: fechas, formato, horario y sede. */}
          </div>

          <ul className={styles.detalles} role="list">
            {detalles.map((detalle) => (
              <li className={styles.detalle} key={detalle.id}>
                <span className={styles.detalleIcono} aria-hidden="true">
                  <OrnamentIcon name={detalle.icon} size={22} />
                </span>
                <span className={styles.detalleTexto}>
                  <span className={styles.detalleEtiqueta}>{detalle.etiqueta}</span>
                  <span className={styles.detalleValor}>{detalle.valor}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className={styles.reservaCta}>
            <TicketCta event={event} variant={variant} size="lg" />
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/**
 * EL BOTÓN DENTRO DEL HERO, colgando de la última línea de la promesa.
 *
 * Es la petición literal del cliente y la que más veces tuve que releer:
 *
 *   «debajo de recuperar tu [auto]maestría, nos gustaría ya meter un CTA de
 *    compra, es decir, **reserva mi lugar o compra mi entrada**» (0:50)
 *
 * Yo lo había resuelto con la tarjeta de detalle en la sección siguiente, y no
 * es lo mismo: entre la promesa y la tarjeta quedaba el aire de cierre del hero,
 * y ahí es donde el cliente señalaba con el ratón. Lo que pedían es un BOTÓN, en
 * ese hueco, dentro del hero — el gesto de comprar disponible en el primer
 * pantallazo, sin scroll y sin leer nada más.
 *
 * La tarjeta de detalle se queda donde está y no sobra: son dos momentos
 * distintos. Éste es para quien ya viene decidido del anuncio; aquélla para
 * quien necesita saber fechas, horario y sede antes de pulsar.
 *
 * DESVIACIÓN DE MOCKUP, DECLARADA: ninguno de los cinco pósters dibuja un botón
 * en el hero. Manda el cliente sobre su propia campaña (ADR-006).
 */
export function HeroReservaCta({
  event,
  variant = 'standard',
  extra,
}: {
  event: TourEvent;
  variant?: 'standard' | 'premium';
  /**
   * Segundo control bajo el de compra. Hoy: el vídeo de la ciudad.
   *
   * POR QUÉ SUBIÓ AQUÍ, DESDE LA BANDA. El 12-sep-2026 se colocó en la banda
   * que sigue al programa, por coherencia con `/cancun`. Medido en la página
   * publicada, eso lo dejaba **a 2.827 px del borde superior, el 38 % de la
   * página, tres pantallas de scroll**. Un VSL —que es un argumentario de
   * venta— ahí no lo ve casi nadie, y el propio ORACLE no lo encontraba. La
   * coherencia de sistema no vale un vídeo invisible: sube al primer
   * pantallazo, que es donde aterriza el tráfico de campaña.
   */
  extra?: ReactNode;
}) {
  return (
    <div className={styles.heroCta}>
      {/* EL VÍDEO VA ENCIMA DEL BOTÓN DE COMPRA, entre la promesa y el CTA.
          Encargo del ORACLE (12-sep-2026): «quizá el botón debe ir entre el
          texto y el CTA». Y tiene sentido de embudo: quien acaba de leer la
          promesa duda, y lo que resuelve la duda es oír al mentor; el botón de
          comprar queda al final, que es donde se decide. La jerarquía no la
          pierde por ir debajo: es navy pleno frente al oro del vídeo. */}
      {extra}
      <TicketCta event={event} variant={variant} size="lg" />
    </div>
  );
}

export interface CtaBandProps {
  event: TourEvent;
  variant?: 'standard' | 'premium';
  background?: 'surface' | 'alt';
  /** Frase corta a la izquierda del botón. Por defecto, la del programa. */
  texto?: string;
  /**
   * Segundo control al lado del CTA de compra. Encargo del cliente
   * (12-sep-2026) para `/cancun`: el botón del vídeo de la convivencia
   * anterior en Palmaïa va justo aquí, pegado al de reserva.
   *
   * ES OPCIONAL Y POR ESO NO ROMPE LAS OTRAS CUATRO. La banda la comparten los
   * cinco destinos; una sección nueva sólo para Cancún habría duplicado el
   * bloque entero para añadirle un botón, y un botón cableado dentro de la
   * banda se lo habría comido `/miami` sin tener vídeo que enseñar.
   */
  extra?: ReactNode;
}

/**
 * BANDA DE CTA COMPACTA — una línea y un botón, nada más.
 *
 * Es el «arriba de esto y justo debajo también, meter el comprar la entrada»
 * del encargo, dicho mientras miraban el PROGRAMA. La intención de compra
 * aparece justo cuando alguien acaba de leer a qué hora es cada bloque y qué se
 * hace en cada uno: ése es el momento en que decide, y no puede tener que
 * buscar el botón.
 *
 * POR QUÉ NO ES OTRO `ReservaCta`. El bloque de detalle ya dio las fechas, el
 * formato, el horario y la sede tres secciones más arriba. Repetirlos aquí sería
 * ruido, y además dos tarjetas grandes idénticas en la misma página se leen como
 * un error de maquetación, no como una segunda oportunidad de comprar.
 */
export function CtaBand({ event, variant = 'standard', background = 'surface', texto, extra }: CtaBandProps) {
  /* Sin escasez y sin cifras: ADR-003 §6 prohíbe «plazas restantes», y una frase
   * que roce el «quedan pocas» es exactamente lo que no se puede escribir aquí.
   * Esto es descriptivo — cuántos días y dónde — y nada más. El número de días
   * se lee del programa: dos en las conferencias, tres en el retiro. */
  const dias = event.program.length;
  const linea =
    texto ??
    (event.format === 'retiro'
      ? `${dias} días de convivencia en ${event.shortName}, en petit comité.`
      : `${dias} días de inmersión en ${event.shortName}, con Azrahel del Mayor.`);

  return (
    <Section background={background} width="default" className={styles.bandaSection}>
      <Reveal>
        <div className={styles.banda}>
          <p className={styles.bandaTexto}>{linea}</p>
          {/* Los dos botones forman una columna propia: en escritorio quedan
              uno debajo del otro a la derecha del texto —que es como los pidió
              el cliente— y en móvil la banda entera sigue siendo una columna. */}
          <div className={styles.bandaAcciones}>
            <TicketCta event={event} variant={variant} size="lg" className={styles.bandaCta} />
            {extra}
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

interface Razon {
  readonly id: string;
  readonly icon: OrnamentIconName;
  readonly titulo: string;
  readonly texto: string;
}

/**
 * Las tres razones. DOS son constantes del producto y la TERCERA es la ciudad.
 *
 * Las dos primeras no son copy inventado: la Cámara Cuántica Pleyadiana y la
 * presencia de Azrahel están en el programa de los cinco destinos, comprobable
 * en `event.program`. La tercera sale del PROGRAMA de esa ciudad — así «adaptar
 * el texto a cada ciudad» deja de ser una tarea de mantenimiento y pasa a ser
 * una propiedad del componente.
 *
 * LA TERCERA RAZÓN EMPEZÓ SIENDO `titleMain` + `tagline` Y ESO ESTABA MAL: es
 * exactamente lo que el hero acaba de decir tres pantallas más arriba, así que
 * la razón que debía justificar ESTA ciudad no aportaba nada nuevo — en
 * `/buenos-aires` se leía «Desprogramación de la Percepción» dos veces seguidas.
 * Los títulos de las jornadas sí son información que el visitante no ha visto
 * todavía, y son distintos en las cinco.
 */
function razonesDe(event: TourEvent): readonly Razon[] {
  const dias = getEventDayCount(event);
  /* LA CAMARA CUANTICA SOLO SE PROMETE DONDE LA ANUNCIA EL CLIENTE. El flyer de
   * Funes no la menciona —habla de convivencia, naturaleza y sanacion—, asi que
   * ahi esta razon NO SE CONSTRUYE: prometer una sesion que nadie ha anunciado
   * es justo lo que prohibe el ADR-003. El indicador es el mismo que la foto:
   * si no hay Camara que ensenar, no hay Camara que vender. */
  const camara: readonly Razon[] =
    CAMARA_POR_CIUDAD[event.slug] === undefined
      ? []
      : [
          {
            id: 'camara',
            icon: 'seed',
            titulo: 'La Cámara Cuántica Pleyadiana',
            texto:
              'Sesión grupal en vivo: calibración del campo y salto de frecuencia. No es una charla sobre la Cámara, es entrar en ella.',
          },
        ];

  return [
    ...camara,
    {
      id: 'azrahel',
      icon: 'spiral',
      titulo: 'Azrahel del Mayor, en persona',
      texto:
        'Guía, canal y semilla estelar. Sabiduría ancestral y frecuencias pleyadianas, transmitidas en directo y sin intermediarios.',
    },
    {
      id: 'ciudad',
      icon: 'star',
      titulo: `${dias} días en ${event.shortName}`,
      /* Los títulos que el propio cliente le puso a cada jornada, en orden. Con
       * el retiro son tres y con las conferencias dos: se leen del dato, no se
       * cuentan a mano. Y donde todavía no hay programa, se dice. */
      texto:
        event.program.length === 0
          ? 'El programa por jornadas se anunciará antes del encuentro.'
          : event.program.map((day) => day.title).join(' · '),
    },
  ];
}

export interface PorQueIrProps {
  event: TourEvent;
  variant?: 'standard' | 'premium';
  background?: 'surface' | 'alt';
}

/**
 * «POR QUÉ IR» — el argumentario, con la foto de la Cámara de esta sede.
 *
 * El párrafo grande es `salesHook`: **lo escribió el cliente, ciudad por
 * ciudad**, y hasta hoy no se veía en ninguna landing salvo la de Cancún. No se
 * reescribe ni se resume; se pinta.
 *
 * Cierra con el segundo CTA de compra, que es el «arriba de esto y justo debajo
 * también meter el comprar la entrada» del encargo: el visitante que baja
 * leyendo el argumentario se encuentra el botón en el momento en que el
 * argumento acaba de convencerle, sin tener que buscarlo.
 */
export function PorQueIr({ event, variant = 'standard', background = 'surface' }: PorQueIrProps) {
  const tituloId = `por-que-ir-${event.slug}`;
  const razones = razonesDe(event);
  const camara = CAMARA_POR_CIUDAD[event.slug];

  return (
    <Section background={background} labelledBy={tituloId} width="default">
      <div className={styles.porque}>
        {camara !== undefined && (
          <Reveal className={styles.porqueMedia}>
            <AssetImage
              asset={camara}
              className={styles.porqueImg}
              sizes="(min-width: 64rem) 42vw, 100vw"
            />
          </Reveal>
        )}

        <div className={styles.porqueCopy}>
          <Reveal>
            <Eyebrow ink="gold">Por qué ir</Eyebrow>
            <h2 className={styles.porqueTitulo} id={tituloId}>
              Por qué {event.shortName}
            </h2>
            <GoldLine ornament="star" width="sm" className={styles.porqueFilete} />
          </Reveal>

          {/* El gancho del cliente, íntegro. Es el texto más importante de la
              sección y por eso va antes que las razones, no de pie de foto. */}
          <Reveal delay={0.06}>
            <p className={styles.porqueHook}>{event.salesHook}</p>
          </Reveal>

          <ul className={styles.razones} role="list">
            {razones.map((razon, index) => (
              <Reveal as="li" className={styles.razon} delay={0.12 + index * 0.06} key={razon.id}>
                <span className={styles.razonIcono} aria-hidden="true">
                  <OrnamentIcon name={razon.icon} size={28} />
                </span>
                <div className={styles.razonCuerpo}>
                  <h3 className={styles.razonTitulo}>{razon.titulo}</h3>
                  <p className={styles.razonTexto}>{razon.texto}</p>
                </div>
              </Reveal>
            ))}
          </ul>

          <Reveal delay={0.3} className={styles.porqueCta}>
            <TicketCta event={event} variant={variant} size="lg" />
          </Reveal>
        </div>
      </div>
    </Section>
  );
}
